import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import StickyButton from '@/components/atom/buttons/StickyButton';
import useCanProgressQnaWrite from '@/hooks/qna/mobile/Write/useCanProgressQnaWrite';
import { QuestionCategoryType } from '@/hooks/qna/useQuestionDetailViewModel';
import { mobileLogger } from '@/utils/logger';

interface Props {
  pageName: string;
  buttonText: string;
  sectionName?: string;
  isPaymentFailed?: boolean;
  shouldShowEventText?: boolean;
  onClickRepay?: () => void;
  onClickButton?: () => void;
  questionCategory?: QuestionCategoryType;
}
/**
 *
 * @param pageName: string
 * @param buttonText: string
 * @param sectionName?: string
 *
 */

const QnaStickyWriteButton: FunctionComponent<Props> = ({
  pageName,
  buttonText,
  sectionName,
  isPaymentFailed,
  shouldShowEventText,
  onClickRepay,
  onClickButton,
  questionCategory,
}) => {
  const { clickProgressWrite } = useCanProgressQnaWrite();

  const onClickWriteButton = async () => {
    mobileLogger({
      logger: {
        eventName: 'write_question_sticky_click',
        pageName,
        payload: {
          sectionName: sectionName ?? 'question_list',
        },
      },
    });

    if (onClickButton) {
      onClickButton();
      return;
    }

    await clickProgressWrite();
  };

  const EventContent = () => {
    if (!shouldShowEventText || !questionCategory) {
      return null;
    }

    if (questionCategory.isRecommendedTreatment) {
      switch (questionCategory.categoryCode) {
        default:
          return <></>;
      }
    }

    return (
      <EventTextArea>
        <EventText>의료진 첫 답변</EventText>
        <HighlightedEventText>0원!</HighlightedEventText>
        <LineThroughEventText>1,900원</LineThroughEventText>
      </EventTextArea>
    );
  };

  return (
    <StickyButton>
      <StickyArea>
        {isPaymentFailed && (
          <RepayButton onClick={onClickRepay}>
            <img alt={'waring'} src={'/images/qna/warning.webp'} width={36} height={36} />
            <RepayButtonContent>
              <RepayButtonTitle>
                결제에 실패했어요
                <svg
                  width='16'
                  height='16'
                  preserveAspectRatio='none'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M 5.301 11.625 L 8.723 8.281 L 5.301 4.953 L 6.238 4 L 10.645 8.281 L 6.238 12.578 L 5.301 11.625 Z'
                    fill='white'
                  />
                </svg>
              </RepayButtonTitle>
              <RepayButtonDescription>결제방법을 확인하거나 변경해주세요</RepayButtonDescription>
            </RepayButtonContent>
          </RepayButton>
        )}
        <StickyButton.Button text={buttonText} onClick={onClickWriteButton} />
        <EventContent />
      </StickyArea>
    </StickyButton>
  );
};

export default QnaStickyWriteButton;

const StickyArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  div + div {
    margin: 0px;
  }
`;

const RepayButton = styled.div`
  display: flex;
  padding: 22px 18px;
  align-items: center;
  gap: 8px;

  border-radius: 16px;
  background: rgba(0, 0, 0, 0.8);
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(4px);
  margin-bottom: 25px;
`;

const RepayButtonContent = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const RepayButtonTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${({ theme }) => `
    ${theme.typography.UI17SB};
    color: ${theme.color.white};
  `};
`;
const RepayButtonDescription = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI15R};
    color: ${theme.color.gray200};
  `};
`;

const EventTextArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 8px;
  gap: 4px;
`;

const EventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI15M};
    color: ${theme.color.gray500};
  `};
`;

const HighlightedEventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI15SB};
    color: ${theme.color.primary700};
  `};
`;

const _EventTextWrapper = styled.span``;

const LineThroughEventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI12R};
    color: ${theme.color.gray300};
  `};
  text-decoration: line-through;
`;
